import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { consoleApi } from '@/lib/consoleApi';
import { adminApi, BetaExclusionRefusal } from '@/lib/adminApi';
import { strataLabel } from '@/lib/periReview';
import { PARTICIPANT_GATE_STATUSES, STRATA_TARGETS, type BetaParticipant } from '@/lib/betaConsole';
import { LoadingNote, ErrorNote, EmptyNote } from '@/admin';

/** Cohort composition and per-participant scheduling.
 *
 *  Identity, strata and blinded assignment are NOT owned here — they live in the
 *  peri-review cohort store and are read through `cohort_status` / the queue.
 *  This tab adds only what that store does not carry: draw dates, interview
 *  dates, the activation gate the last run resolved to, and the two numbers
 *  worth watching per run (overall_confidence, panel coverage). */
export function CohortTab() {
  const queue = useQuery({ queryKey: ['peri-review-queue'], queryFn: () => consoleApi.periReview.queue() });
  const beta = useQuery({ queryKey: ['beta-participants'], queryFn: () => adminApi.beta.participants.list() });

  if (queue.isLoading) return <LoadingNote />;
  if (queue.isError) return <ErrorNote>Could not load the cohort.</ErrorNote>;

  const rows = queue.data?.participants ?? [];
  const cohort = queue.data?.cohort_status;
  const betaByRef = new Map<string, BetaParticipant>((beta.data ?? []).map((p) => [p.participant_ref, p]));

  return (
    <>
      {cohort ? (
        <section className="mb-13 rounded border border-border bg-surface p-13">
          <h2 className="mb-2 text-body font-semibold text-text-primary">Cohort</h2>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Stratum</th><th className="w-20">Target</th><th className="w-20">Now</th><th className="w-28">Status</th></tr>
              </thead>
              <tbody>
                <TargetRow label="Consented" target={cohort.target.size_min} now={cohort.size} />
                <TargetRow label="Blinded review" target={cohort.target.blinded_min} now={cohort.blinded} />
                {Object.entries(cohort.strata_coverage).map(([k, n]) => (
                  <TargetRow key={k} label={humaniseStratum(k)} target={0} now={n} />
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-body-sm text-text-muted">
            Size, blinded split and strata coverage come from the peri-review cohort store — the
            source of truth for who is in the cohort. Assign participants on{' '}
            <Link className="text-accent hover:underline" to="/admin/testers">Testers &amp; invites</Link>.
          </p>
        </section>
      ) : null}

      <EnrolCard />

      <section className="mb-13 rounded border border-border bg-surface p-13">
        <h2 className="mb-2 text-body font-semibold text-text-primary">Screening strata</h2>
        <p className="mb-2 text-body-sm text-text-muted">
          Screened for, not assigned. These are not tracked by the cohort store — they are recruitment targets.
        </p>
        <div className="tbl-wrap">
          <table className="tbl">
            <tbody>
              {STRATA_TARGETS.map((s) => (
                <tr key={s.key}>
                  <td className="w-1/4 align-top font-semibold text-text-primary">{s.label}</td>
                  <td className="w-16 align-top font-mono text-[12px]">≥{s.target}</td>
                  <td className="align-top text-text-secondary">{s.rationale}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-13 rounded border border-border bg-surface p-13">
        <h2 className="mb-2 text-body font-semibold text-text-primary">
          Participants <span className="text-body-sm font-normal text-text-muted">· {rows.length}</span>
        </h2>
        {beta.isError ? (
          <p className="mb-2 text-body-sm text-flagged">
            Scheduling fields unavailable — <code>/admin/beta/participants</code> is not deployed here yet.
            Cohort identity below is still live.
          </p>
        ) : null}
        {rows.length === 0 ? (
          <EmptyNote>
            No participants in the cohort yet. Recruitment is not downstream of the build — screening opens first.
          </EmptyNote>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Participant</th>
                  <th>Strata</th>
                  <th className="w-20">Mode</th>
                  <th className="w-28">Baseline draw</th>
                  <th className="w-28">T3 draw</th>
                  <th className="w-40">Gate status</th>
                  <th className="w-20">Conf</th>
                  <th className="w-20">Coverage</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const b = betaByRef.get(r.participant_ref);
                  return (
                    <tr key={r.participant_ref}>
                      <td className="font-mono text-[12px]">
                        <Link className="text-accent hover:underline" to={`/review/peri/${encodeURIComponent(r.participant_ref)}`}>
                          {r.participant_ref}
                        </Link>
                      </td>
                      <td className="text-[12px] text-text-secondary">{strataLabel(r.strata_stage, r.strata_subbranch)}</td>
                      <td>
                        <span className={`inline-block rounded-pill px-2 py-0.5 text-[11px] font-semibold ${r.review_mode === 'blinded' ? 'mod-sign' : 'mod-form'}`}>
                          {r.review_mode}
                        </span>
                      </td>
                      <td className="font-mono text-[11px]">{b?.draw1 || '—'}</td>
                      <td className="font-mono text-[11px]">{b?.draw3 || '—'}</td>
                      <td className="font-mono text-[11px]">
                        {b?.gate_status && PARTICIPANT_GATE_STATUSES.includes(b.gate_status) ? b.gate_status : '—'}
                      </td>
                      <td className="font-mono text-[11px]">{b?.overall_confidence ?? '—'}</td>
                      <td className="font-mono text-[11px]">{b?.panel_coverage ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

/** Enrolment. The oestrogen-sensitive exclusion is NOT enforced here — the
 *  server refuses it on POST while the botanical gate is open, and this form
 *  reports that refusal. A client-side guard on an MA-owned exclusion criterion
 *  could only ever make the wrong intake hard by accident, not impossible. */
function EnrolCard() {
  const qc = useQueryClient();
  const [userRef, setUserRef] = useState('');
  const [oestrogenSensitive, setOestrogenSensitive] = useState(false);
  const [refusal, setRefusal] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const enrol = useMutation({
    mutationFn: () => adminApi.beta.participants.enrol(userRef.trim(), oestrogenSensitive),
    onMutate: () => { setRefusal(''); setError(''); setOk(''); },
    onSuccess: () => {
      setOk(`Enrolled ${userRef.trim()}.`);
      setUserRef(''); setOestrogenSensitive(false);
      void qc.invalidateQueries({ queryKey: ['beta-participants'] });
    },
    onError: (e: unknown) => {
      // An exclusion is an answer, not a failure — say which one it was.
      if (e instanceof BetaExclusionRefusal) setRefusal(e.message);
      else setError(e instanceof Error ? e.message : 'Enrolment failed.');
    },
  });

  return (
    <section className="mb-13 rounded border border-border bg-surface p-13">
      <h2 className="mb-2 text-body font-semibold text-text-primary">Enrol a participant</h2>
      <p className="mb-2 text-body-sm text-text-muted">
        Screening result, recorded at intake. The exclusion is enforced server-side against the
        botanical safety gate — this form reports the refusal, it does not make it.
      </p>
      <div className="mb-2 flex flex-wrap items-end gap-3">
        <label className="text-[11px] font-semibold uppercase text-text-muted">
          User ref
          <input
            className="searchbox !mb-0 mt-1"
            value={userRef}
            onChange={(e) => setUserRef(e.target.value)}
            placeholder="user-…"
          />
        </label>
        <label className="flex items-center gap-2 pb-2 text-body-sm text-text-primary">
          <input
            type="checkbox"
            checked={oestrogenSensitive}
            onChange={(e) => setOestrogenSensitive(e.target.checked)}
          />
          Oestrogen-sensitive history
        </label>
        <button
          type="button"
          className="btn mb-1"
          disabled={!userRef.trim() || enrol.isPending}
          onClick={() => enrol.mutate()}
        >
          Enrol
        </button>
      </div>
      {refusal ? (
        <div className="rounded border-l-2 border-flagged bg-surface-sunken p-13">
          <p className="text-body-sm text-text-primary"><strong>Refused at intake.</strong> {refusal}</p>
        </div>
      ) : null}
      {error ? <p className="text-body-sm text-flagged">{error}</p> : null}
      {ok ? <p className="text-body-sm text-accent">{ok}</p> : null}
    </section>
  );
}

/** strata_coverage is keyed by the store's enum values (early_transition,
 *  nd_affirming …). Render them as prose — the raw key in a table read as a bug
 *  the first time it was seen. Unknown keys pass through rather than vanish. */
function humaniseStratum(key: string): string {
  const known: Record<string, string> = {
    early_transition: 'Early transition',
    late_transition: 'Late transition',
    base: 'base sub_branch',
    nd_affirming: 'ND-affirming sub_branch',
  };
  return known[key] ?? key.replace(/_/g, ' ');
}

function TargetRow({ label, target, now }: { label: string; target: number; now: number }) {
  const met = target > 0 && now >= target;
  return (
    <tr>
      <td>{label}</td>
      <td className="font-mono text-[12px]">{target > 0 ? target : '—'}</td>
      <td className="font-mono text-[12px]"><strong>{now}</strong></td>
      <td>
        {target > 0 ? (
          <span className={`inline-block rounded-pill px-2 py-0.5 text-[11px] font-semibold ${met ? 'mod-sign' : 'mod-form'}`}>
            {met ? 'met' : `${target - now} short`}
          </span>
        ) : <span className="text-text-muted">—</span>}
      </td>
    </tr>
  );
}
